import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronDown, ChevronsUpDown, Loader2, Sparkles, X } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { friendlyMessage } from '@/api/http'
import { autopilotApi, waitForSkeletonJob } from '@/api/autopilotApi'
import { chapterApi } from '@/api/chapterApi'
import { genreTemplateApi } from '@/api/genreTemplateApi'
import { projectApi, type CreateProjectInput } from '@/api/projectApi'
import { skeletonApi } from '@/api/skeletonApi'
import { characterApi } from '@/api/characterApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { resolveLocalized } from '@/i18n/resolveLocalized'
import { useUiStore } from '@/stores/uiStore'
import type { ConceptSuggestion, GenreTemplate, Project } from '@/types/nf'
import { cn } from '@/lib/utils'
import { CONTENT_LANGUAGES, defaultContentLanguage } from '@/lib/contentLanguages'
import { secondaryDriverLabel } from '@/lib/secondaryDrivers'
import { VoiceInputButton } from '@/components/voice/VoiceInputButton'
import {
  EasyGenreFallback,
  AutopilotCreationOption,
  applyProjectDraft,
  createDefaultProjectWizardValues,
  projectDraftOutcome,
  targetWordsByLength,
  type ProjectWizardValues,
} from '@/components/project/projectWizardEasy'
import {
  AutopilotCreationError,
  runAutopilotCreation,
  type AutopilotCreationResume,
  type AutopilotCreationStage,
} from '@/components/project/autopilotCreation'

type AssistField = 'core_theme' | 'logline'

interface AssistState {
  field: AssistField
  suggestions: ConceptSuggestion[]
  source: 'llm' | 'template_fallback'
}

interface ProjectCreateSubmission {
  create: CreateProjectInput
  world: {
    world_time_period?: string
    world_location?: string
    world_atmosphere?: string
    world_rules?: string
  }
  protagonist?: {
    name: string
    name_en?: string
    motivation?: string
    inner_flaw?: string
    identity_situation?: string
  }
}

interface ProjectCreateWorkflowSubmission extends ProjectCreateSubmission {
  autopilot: {
    enabled: boolean
    targetWords: number
    chapterWordTarget: number
    branchId: string
  }
}

interface CreateProjectWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId?: string
}

const textAreaClassName =
  'min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50'

const featuredGenreKeys = ['xuanhuan_xianxia', 'urban_realism', 'scifi', 'wuxia', 'mystery_suspense', 'romance']
function SummarySection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Collapsible className="rounded-lg border bg-background/40">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {title}
          <ChevronDown className="size-4 text-muted-foreground" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-4 py-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}

function optionalText(value?: string) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function fallbackTitle(
  values: ProjectWizardValues,
  selectedTemplate: GenreTemplate | undefined,
  lang: string,
  t: TFunction,
) {
  const explicitTitle = optionalText(values.title)
  if (explicitTitle) return explicitTitle
  const fromLogline = optionalText(values.logline)?.slice(0, 36)
  if (fromLogline) return fromLogline
  const label = resolveLocalized(selectedTemplate?.payload.genre_label, lang) || values.genre_key
  return label ? t('project.wizard.fallbackTitle', { label }) : t('project.wizard.untitledProject')
}

export function CreateProjectWizard({ open, onOpenChange, userId }: CreateProjectWizardProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const [driverOpen, setDriverOpen] = useState(false)
  const [assistState, setAssistState] = useState<AssistState | null>(null)
  const contentMode = useUiStore((s) => s.contentMode)
  const setContentMode = useUiStore((s) => s.setContentMode)
  // 向导在弹窗里, 标题行的全局开关此时不可见 -> 保留自己的开关,
  // 但与全局 contentMode 同源: 初始值取它, 切换也写回它。
  const [mode, setMode] = useState<'easy' | 'advanced'>(contentMode)
  const [idea, setIdea] = useState('')
  const [easyStage, setEasyStage] = useState<'input' | 'summary' | 'genre_fallback'>('input')
  const [fallbackSuggestions, setFallbackSuggestions] = useState<ConceptSuggestion[]>([])
  const [draftGenreLabel, setDraftGenreLabel] = useState('')
  const [autopilotResume, setAutopilotResume] = useState<AutopilotCreationResume>({})
  const [autopilotStage, setAutopilotStage] = useState<AutopilotCreationStage>()
  const [autopilotErrorStage, setAutopilotErrorStage] = useState<AutopilotCreationStage>()

  const form = useForm<ProjectWizardValues>({
    defaultValues: createDefaultProjectWizardValues(),
  })

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().trim().max(120, t('project.titleTooLong')).optional(),
        description: z.string().trim().max(800, t('project.descriptionTooLong')).optional(),
        core_theme: z.string().trim().max(500, t('project.wizard.validation.coreThemeTooLong')).optional(),
        genre_key: z.string().trim().min(1, t('project.wizard.validation.genreRequired')),
        target_words: z.number().int().positive(t('project.targetWordsPositive')),
        chapter_word_target: z.number().int().positive(t('project.targetWordsPositive')),
        autopilot_enabled: z.boolean(),
        secondary_drivers: z.array(z.string()).optional(),
        logline: z.string().trim().max(600, t('project.wizard.validation.loglineTooLong')).optional(),
        antagonist_source: z.string().trim().max(300, t('project.wizard.validation.antagonistTooLong')).optional(),
        tone_keynote: z.string().trim().max(200, t('project.wizard.validation.toneTooLong')).optional(),
        narrative_perspective: z.string().trim().max(120, t('project.wizard.validation.perspectiveTooLong')).optional(),
      }),
    [t],
  )

  const templatesQuery = useQuery({
    queryKey: ['genre-templates'],
    queryFn: genreTemplateApi.list,
    enabled: open,
  })
  const projectOptionsQuery = useQuery({
    queryKey: ['project-options'],
    queryFn: projectApi.options,
    enabled: open,
  })

  const selectedGenreKey = form.watch('genre_key')
  const targetWords = form.watch('target_words')
  const chapterWordTarget = form.watch('chapter_word_target')
  const autopilotEnabled = form.watch('autopilot_enabled')
  const secondaryDrivers = form.watch('secondary_drivers')
  const selectedTemplateFromList = useMemo(
    () => templatesQuery.data?.find((template) => template.genre_key === selectedGenreKey),
    [selectedGenreKey, templatesQuery.data],
  )

  const selectedTemplate = selectedTemplateFromList
  const featuredTemplates = useMemo(() => {
    const templates = templatesQuery.data ?? []
    const byKey = new Map(templates.map((template) => [template.genre_key, template]))
    return featuredGenreKeys.map((key) => byKey.get(key)).filter((template): template is GenreTemplate => Boolean(template))
  }, [templatesQuery.data])
  const payload = selectedTemplate?.payload
  const lang = i18n.resolvedLanguage ?? i18n.language
  const contentLang = form.watch('content_language') || defaultContentLanguage(lang)
  const genreLabel = resolveLocalized(payload?.genre_label, contentLang) || selectedGenreKey
  const worldRulesLabel = resolveLocalized(payload?.world_rules_label, lang) || t('project.wizard.worldRules')
  const loglinePrompt = resolveLocalized(payload?.logline_prompt, lang)
  const worldTimePeriod = resolveLocalized(payload?.world_defaults?.time_period, contentLang)
  const worldLocation = resolveLocalized(payload?.world_defaults?.location, contentLang)
  const worldAtmosphere = resolveLocalized(payload?.world_defaults?.atmosphere, contentLang)
  const protagonistStartSituation = resolveLocalized(payload?.protagonist_paradigm?.start_situation, contentLang)
  const protagonistInnerFlaw = resolveLocalized(payload?.protagonist_paradigm?.inner_flaw, contentLang)
  const secondaryDriverOptions = projectOptionsQuery.data?.secondary_drivers ?? []

  const autopilotEstimateQuery = useQuery({
    queryKey: ['autopilot-estimate', targetWords, chapterWordTarget, selectedGenreKey],
    queryFn: () => autopilotApi.estimate({
      target_words: targetWords as number,
      chapter_word_target: chapterWordTarget,
      genre: genreLabel,
      scope: 'volume',
    }),
    enabled: open && mode === 'easy' && easyStage !== 'input' && Boolean(selectedGenreKey && targetWords && chapterWordTarget),
  })
  const secondaryDriverByKey = useMemo(
    () => new Map(secondaryDriverOptions.map((driver) => [driver.key, driver])),
    [secondaryDriverOptions],
  )

  useEffect(() => {
    if (!payload) return
    if (!form.getFieldState('secondary_drivers').isDirty) {
      form.setValue('secondary_drivers', payload.default_driver_combo ?? [], { shouldDirty: false })
    }
    if (!form.getFieldState('world_time_period').isDirty) {
      form.setValue('world_time_period', resolveLocalized(payload.world_defaults?.time_period, contentLang), { shouldDirty: false })
    }
    if (!form.getFieldState('world_location').isDirty) {
      form.setValue('world_location', resolveLocalized(payload.world_defaults?.location, contentLang), { shouldDirty: false })
    }
    if (!form.getFieldState('world_atmosphere').isDirty) {
      form.setValue('world_atmosphere', resolveLocalized(payload.world_defaults?.atmosphere, contentLang), { shouldDirty: false })
    }
    if (!form.getFieldState('target_words').isDirty && !form.getValues('target_words')) {
      form.setValue('target_words', 800000, { shouldDirty: false })
    }
  }, [form, contentLang, payload])

  const createMutation = useMutation({
    mutationFn: async ({ create, world, protagonist, autopilot }: ProjectCreateWorkflowSubmission) => {
      const createProject = async () => {
        const created = await projectApi.create(create)
        let project = created
        let worldSaved = true
        if (Object.keys(world).length > 0) {
          try {
            project = await projectApi.patch(created.id, world)
          } catch {
            worldSaved = false
          }
        }
        // S4-4 主角衔接: 向导主角字段非空 → 落成一个 role=protagonist 角色(解 S3b 挂起项)。
        // 角色建失败不阻断项目创建(同 world patch 容错)。
        if (protagonist?.name) {
          try {
            await characterApi.create(created.id, {
              name: protagonist.name,
              name_en: protagonist.name_en ?? null,
              role: 'protagonist',
              motivation: protagonist.motivation ?? null,
              inner_flaw: protagonist.inner_flaw ?? null,
              identity_situation: protagonist.identity_situation ?? null,
            })
          } catch {
            /* 主角角色建失败仅记略, 项目已创建成功 */
          }
        }
        return { project, worldSaved }
      }

      if (!autopilot.enabled) return createProject()

      let worldSaved = true
      const result = await runAutopilotCreation(
        {
          targetWords: autopilot.targetWords,
          chapterWordTarget: autopilot.chapterWordTarget,
          branchId: autopilot.branchId,
        },
        {
          createProject: async () => {
            const created = await createProject()
            worldSaved = created.worldSaved
            return created.project
          },
          proposeSkeleton: skeletonApi.propose,
          waitForSkeletonJob,
          confirmSkeleton: skeletonApi.confirm,
          confirmOutline: chapterApi.confirmOutline,
          startAutopilot: autopilotApi.start,
        },
        autopilotResume,
        (stage) => {
          setAutopilotErrorStage(undefined)
          setAutopilotStage(stage)
        },
      )
      return { project: result.project, worldSaved }
    },
    onSuccess: async ({ project, worldSaved }: { project: Project; worldSaved: boolean }) => {
      toast.success(t('project.created'))
      if (!worldSaved) {
        toast.warning(t('project.wizard.worldSaveFailed'))
      }
      form.reset()
      setAssistState(null)
      setMode('easy')
      setIdea('')
      setEasyStage('input')
      setFallbackSuggestions([])
      setDraftGenreLabel('')
      setAutopilotResume({})
      setAutopilotStage(undefined)
      setAutopilotErrorStage(undefined)
      onOpenChange(false)
      await queryClient.invalidateQueries({ queryKey: ['projects', userId, currentBranchId] })
      navigate(`/projects/${project.id}`)
    },
    onError: (error) => {
      if (error instanceof AutopilotCreationError) {
        setAutopilotResume(error.resume)
        setAutopilotStage(undefined)
        setAutopilotErrorStage(error.stage)
        toast.error(friendlyMessage(error.cause, t('common.serviceError')))
        return
      }
      toast.error(friendlyMessage(error, t('common.serviceError')))
    },
  })

  const assistMutation = useMutation({
    mutationFn: (field: AssistField) => {
      const values = form.getValues()
      return projectApi.conceptAssist({
        field,
        genre_key: values.genre_key,
        genre_label: genreLabel,
        content_language: values.content_language || defaultContentLanguage(lang),
        secondary_drivers: values.secondary_drivers,
        logline: optionalText(values.logline),
        selling_point: optionalText(values.description),
        protagonist: {
          name: optionalText(values.protagonist_name),
          motivation: optionalText(values.protagonist_motivation),
          inner_flaw: optionalText(values.protagonist_inner_flaw),
          identity_situation: optionalText(values.protagonist_identity_situation),
        },
        world: {
          time_period: optionalText(values.world_time_period),
          location: optionalText(values.world_location),
          atmosphere: optionalText(values.world_atmosphere),
        },
        use_llm: true,
      })
    },
    onSuccess: (response, field) => {
      setAssistState({ field, suggestions: response.suggestions, source: response.source })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('project.wizard.aiFailed')))
    },
  })

  const projectDraftMutation = useMutation({
    mutationFn: () => {
      const values = form.getValues()
      return projectApi.conceptAssist({
        field: 'project_draft',
        idea: idea.trim(),
        length_form: values.length_form,
        content_language: 'zh',
        use_llm: true,
      })
    },
    onSuccess: (response) => {
      const outcome = projectDraftOutcome(response)
      if (outcome.kind === 'draft') {
        const nextValues = applyProjectDraft(form.getValues(), outcome.draft)
        if (!templatesQuery.data?.some((template) => template.genre_key === nextValues.genre_key)) {
          nextValues.genre_key = 'other_custom'
        }
        form.reset(nextValues, { keepDefaultValues: true })
        form.setValue('secondary_drivers', nextValues.secondary_drivers, { shouldDirty: true })
        setFallbackSuggestions([])
        setDraftGenreLabel(outcome.draft.genre_label ?? '')
        setEasyStage('summary')
        return
      }
      setFallbackSuggestions(outcome.suggestions)
      setDraftGenreLabel('')
      setEasyStage('genre_fallback')
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('project.wizard.aiFailed')))
    },
  })

  const toggleDriver = (driver: string) => {
    const current = form.getValues('secondary_drivers')
    const next = current.includes(driver) ? current.filter((item) => item !== driver) : [...current, driver]
    form.setValue('secondary_drivers', next, { shouldDirty: true })
  }

  const runAssist = (field: AssistField) => {
    if (!form.getValues('genre_key')) {
      toast.error(t('project.wizard.validation.genreRequired'))
      return
    }
    assistMutation.mutate(field)
  }

  const applySuggestion = (suggestion: ConceptSuggestion) => {
    if (!assistState) return
    form.setValue(assistState.field, suggestion.text, { shouldDirty: true })
    setAssistState(null)
  }

  const applyFallbackGenre = (suggestion: ConceptSuggestion) => {
    form.setValue('genre_key', 'other_custom', { shouldDirty: true })
    setDraftGenreLabel(suggestion.text)
  }

  const chooseLength = (length: ProjectWizardValues['length_form']) => {
    form.setValue('length_form', length, { shouldDirty: true })
    form.setValue('target_words', targetWordsByLength[length], { shouldDirty: true })
  }

  const startEasyDraft = () => {
    if (!idea.trim()) {
      toast.error(t('project.wizard.easy.ideaRequired'))
      return
    }
    projectDraftMutation.mutate()
  }

  const openAdvanced = () => {
    if (easyStage === 'input' && !form.getFieldState('content_language').isDirty) {
      form.setValue('content_language', defaultContentLanguage(lang), { shouldDirty: false })
    }
    setMode('advanced')
  }

  // 显式双向切换。回 Easy 只改视图, 不重置已填的表单值 ——
  // 高级模式下填过的字段在切回再切来时仍在, 避免误清用户输入。
  const switchMode = (next: 'easy' | 'advanced') => {
    setContentMode(next)
    if (next === 'advanced') {
      openAdvanced()
      return
    }
    setMode('easy')
  }

  const submit = (values: ProjectWizardValues) => {
    if (!userId) return
    const parsed = schema.safeParse(values)
    if (!parsed.success) {
      const issue = parsed.error.issues[0]
      const field = issue?.path[0]
      if (typeof field === 'string' && field in values) {
        form.setError(field as keyof ProjectWizardValues, { message: issue.message })
      }
      toast.error(issue?.message ?? t('project.validationFailed'))
      return
    }

    createMutation.mutate({
      create: {
        user_id: userId,
        title: fallbackTitle(values, selectedTemplate, lang, t),
        description: optionalText(values.description),
        genre: values.genre_key,
		genre_label_custom: values.genre_key === 'other_custom' ? draftGenreLabel : undefined,
        theme: optionalText(values.core_theme),
        target_words: parsed.data.target_words,
        core_theme: optionalText(values.core_theme),
        content_type: values.content_type,
        length_form: values.length_form,
        secondary_drivers: values.secondary_drivers.length ? values.secondary_drivers : undefined,
        logline: optionalText(values.logline),
        antagonist_source: optionalText(values.antagonist_source),
        tone_keynote: optionalText(values.tone_keynote),
        // 所有向导入口默认手动级联，由用户决定何时进入下一工位。
        cascade_auto_threshold: 'manual',
        content_language: values.content_language || defaultContentLanguage(lang),
        narrative_perspective: optionalText(values.narrative_perspective),
      },
      world: {
        ...(optionalText(values.world_time_period) ? { world_time_period: optionalText(values.world_time_period) } : {}),
        ...(optionalText(values.world_location) ? { world_location: optionalText(values.world_location) } : {}),
        ...(optionalText(values.world_atmosphere) ? { world_atmosphere: optionalText(values.world_atmosphere) } : {}),
        ...(optionalText(values.world_rules) ? { world_rules: optionalText(values.world_rules) } : {}),
      },
      protagonist: optionalText(values.protagonist_name)
        ? {
            name: optionalText(values.protagonist_name) as string,
            name_en: optionalText(values.protagonist_name_en),
            motivation: optionalText(values.protagonist_motivation),
            inner_flaw: optionalText(values.protagonist_inner_flaw),
            identity_situation: optionalText(values.protagonist_identity_situation),
          }
        : undefined,
      autopilot: {
        enabled: values.autopilot_enabled,
        targetWords: parsed.data.target_words,
        chapterWordTarget: parsed.data.chapter_word_target,
        branchId: currentBranchId,
      },
    })
  }

  const fieldError = form.formState.errors
  const isAssisting = assistMutation.isPending
  const isDrafting = projectDraftMutation.isPending
  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && createMutation.isPending) return
    if (!nextOpen) {
      form.reset(createDefaultProjectWizardValues())
      setMode('easy')
      setIdea('')
      setEasyStage('input')
      setFallbackSuggestions([])
      setDraftGenreLabel('')
      setAutopilotResume({})
      setAutopilotStage(undefined)
      setAutopilotErrorStage(undefined)
    }
    onOpenChange(nextOpen)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('project.new')}</DialogTitle>
            <DialogDescription>
              {mode === 'easy' ? t('project.wizard.easy.description') : t('project.wizard.description')}
            </DialogDescription>
            <ToggleGroup
              className="mt-2 grid w-full max-w-xs grid-cols-2"
              type="single"
              value={mode}
              onValueChange={(value) => {
                if (value) switchMode(value as 'easy' | 'advanced')
              }}
              aria-label={t('project.wizard.modeLabel')}
            >
              <ToggleGroupItem value="easy">{t('contentMode.easy')}</ToggleGroupItem>
              <ToggleGroupItem value="advanced">{t('contentMode.advanced')}</ToggleGroupItem>
            </ToggleGroup>
          </DialogHeader>
          <form className="space-y-6" onSubmit={form.handleSubmit(submit)}>
            {mode === 'easy' ? (
              <>
                <section className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-easy-idea" className="text-base font-medium">
                      {t('project.wizard.easy.ideaLabel')}
                    </Label>
                    <textarea
                      id="project-easy-idea"
                      className={cn(textAreaClassName, 'min-h-32 text-base leading-7')}
                      maxLength={800}
                      placeholder={t('project.wizard.easy.ideaPlaceholder')}
                      value={idea}
                      disabled={isDrafting}
                      onChange={(event) => setIdea(event.target.value)}
                    />
                  </div>

                  {easyStage === 'input' ? (
                    <div className="space-y-3">
                      <fieldset className="space-y-2">
                        <legend className="text-sm font-medium">{t('project.wizard.easy.lengthLabel')}</legend>
                        <div className="flex flex-wrap gap-4">
                          {(['short', 'long'] as const).map((length) => (
                            <label key={length} className="flex cursor-pointer items-center gap-2 text-sm">
                              <input
                                type="radio"
                                name="project-easy-length"
                                value={length}
                                checked={form.watch('length_form') === length}
                                disabled={isDrafting}
                                onChange={() => chooseLength(length)}
                              />
                              {t(`project.wizard.lengthForms.${length}`)}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <Button type="button" className="w-full sm:w-auto" disabled={isDrafting} onClick={startEasyDraft}>
                        {isDrafting ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                        {isDrafting ? t('project.wizard.easy.drafting') : t('project.wizard.easy.start')}
                      </Button>
                    </div>
                  ) : null}

                  {easyStage === 'genre_fallback' ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">{t('project.wizard.easy.fallbackHint')}</p>
                      <EasyGenreFallback
                        suggestions={fallbackSuggestions}
                        selectedGenreKey={selectedGenreKey}
                        label={t('project.wizard.easy.chooseGenre')}
                        onSelect={applyFallbackGenre}
                      />
                    </div>
                  ) : null}

                  {easyStage === 'summary' ? (
                    <div className="space-y-3">
                      <div className="rounded-lg border bg-muted/20 px-4 py-3">
                        <p className="text-sm font-medium">{t('project.wizard.easy.summaryTitle')}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {[draftGenreLabel || genreLabel, form.watch('logline')].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <SummarySection title={t('project.wizard.easy.groups.basic')}>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label>{t('project.genre')}</Label>
                            <Select
                              value={selectedGenreKey}
                              onValueChange={(value) => {
                                form.setValue('genre_key', value, { shouldDirty: true })
                                setDraftGenreLabel('')
                              }}
                            >
                              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(templatesQuery.data ?? []).map((template) => (
                                  <SelectItem key={template.genre_key} value={template.genre_key}>
                                    {resolveLocalized(template.payload.genre_label, lang)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="project-easy-title">{t('project.name')}</Label>
                            <Input id="project-easy-title" maxLength={120} {...form.register('title')} />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="project-easy-logline">{t('project.wizard.logline')}</Label>
                            <textarea id="project-easy-logline" className={textAreaClassName} maxLength={600} {...form.register('logline')} />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="project-easy-theme">{t('project.wizard.coreTheme')}</Label>
                            <textarea id="project-easy-theme" className={textAreaClassName} maxLength={500} {...form.register('core_theme')} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="project-easy-tone">{t('project.wizard.toneKeynote')}</Label>
                            <Input id="project-easy-tone" maxLength={200} {...form.register('tone_keynote')} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="project-easy-perspective">{t('detail.narrativePerspective')}</Label>
                            <Input id="project-easy-perspective" maxLength={120} {...form.register('narrative_perspective')} />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label>{t('project.wizard.secondaryDrivers')}</Label>
                            <Popover open={driverOpen} onOpenChange={setDriverOpen}>
                              <PopoverTrigger asChild>
                                <Button type="button" variant="outline" className="w-full justify-between">
                                  <span className="truncate">
                                    {secondaryDrivers.length
                                      ? t('project.wizard.selectedDriverCount', { count: secondaryDrivers.length })
                                      : t('project.wizard.secondaryDriversPlaceholder')}
                                  </span>
                                  <ChevronsUpDown className="size-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[min(38rem,calc(100vw-2rem))] p-0" align="start">
                                <Command>
                                  <CommandInput placeholder={t('project.wizard.driverSearch')} />
                                  <CommandList>
                                    <CommandEmpty>{t('project.wizard.driverEmpty')}</CommandEmpty>
                                    <CommandGroup>
                                      {secondaryDriverOptions.map((driver) => {
                                        const selected = secondaryDrivers.includes(driver.key)
                                        const label = secondaryDriverLabel(driver, contentLang)
                                        return (
                                          <CommandItem key={driver.key} value={label} onSelect={() => toggleDriver(driver.key)}>
                                            <Check className={cn('size-4', selected ? 'opacity-100' : 'opacity-0')} />
                                            {label}
                                          </CommandItem>
                                        )
                                      })}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            <div className="flex min-h-7 flex-wrap gap-2">
                              {secondaryDrivers.map((driver) => (
                                <Badge key={driver} variant="secondary" className="gap-1">
                                  {secondaryDriverLabel(secondaryDriverByKey.get(driver), contentLang) || driver}
                                  <button type="button" onClick={() => toggleDriver(driver)} aria-label={t('project.wizard.removeDriver')}>
                                    <X className="size-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </SummarySection>
                      <SummarySection title={t('project.wizard.easy.groups.world')}>
                        <div className="space-y-2">
                          <Label htmlFor="project-easy-world-rules">{worldRulesLabel}</Label>
                          <textarea id="project-easy-world-rules" className={textAreaClassName} {...form.register('world_rules')} />
                        </div>
                      </SummarySection>
                      <SummarySection title={t('project.wizard.easy.groups.protagonist')}>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="project-easy-protagonist-name">{t('project.wizard.protagonistName')}</Label>
                            <Input id="project-easy-protagonist-name" {...form.register('protagonist_name')} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="project-easy-protagonist-name-en">{t('project.wizard.protagonistNameEn')}</Label>
                            <Input id="project-easy-protagonist-name-en" {...form.register('protagonist_name_en')} />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="project-easy-protagonist-identity">{t('project.wizard.protagonistIdentity')}</Label>
                            <Input id="project-easy-protagonist-identity" {...form.register('protagonist_identity_situation')} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="project-easy-protagonist-flaw">{t('project.wizard.protagonistInnerFlaw')}</Label>
                            <Input id="project-easy-protagonist-flaw" {...form.register('protagonist_inner_flaw')} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="project-easy-protagonist-motivation">{t('project.wizard.protagonistMotivation')}</Label>
                            <Input id="project-easy-protagonist-motivation" {...form.register('protagonist_motivation')} />
                          </div>
                        </div>
                      </SummarySection>
                      <SummarySection title={t('project.wizard.easy.groups.antagonist')}>
                        <div className="space-y-2">
                          <Label htmlFor="project-easy-antagonist">{t('project.wizard.antagonistSource')}</Label>
                          <Input id="project-easy-antagonist" maxLength={300} {...form.register('antagonist_source')} />
                        </div>
                      </SummarySection>
                      <AutopilotCreationOption
                        enabled={autopilotEnabled}
                        targetWords={targetWords}
                        chapterWordTarget={chapterWordTarget}
                        estimate={autopilotEstimateQuery.data}
                        estimateLoading={autopilotEstimateQuery.isFetching}
                        busyStage={autopilotStage}
                        errorStage={autopilotErrorStage}
                        disabled={createMutation.isPending || Boolean(autopilotResume.project)}
                        onEnabledChange={(value) => form.setValue('autopilot_enabled', value, { shouldDirty: true })}
                        onTargetWordsChange={(value) => form.setValue('target_words', value, { shouldDirty: true })}
                        onChapterWordTargetChange={(value) => form.setValue('chapter_word_target', value ?? 3500, { shouldDirty: true })}
                      />
                    </div>
                  ) : null}
                </section>

                {easyStage !== 'input' ? (
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending || !selectedGenreKey}>
                      {createMutation.isPending
                        ? t(`project.wizard.autopilot.stages.${autopilotStage ?? 'create_project'}`)
                        : autopilotErrorStage
                          ? t('project.wizard.autopilot.retry')
                          : t('project.wizard.easy.createProject')}
                    </Button>
                  </DialogFooter>
                ) : null}
              </>
            ) : (
              <>
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-medium">{t('project.wizard.coreTheme')}</h2>
                  <p className="text-sm text-muted-foreground">{t('project.wizard.coreThemeHint')}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => runAssist('core_theme')}
                  disabled={isAssisting}
                >
                  {isAssisting && assistMutation.variables === 'core_theme' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Sparkles className="size-4" />
                  )}
                  {t('project.wizard.aiCoreTheme')}
                </Button>
              </div>
              <textarea
                className={textAreaClassName}
                maxLength={500}
                placeholder={t('project.wizard.coreThemePlaceholder')}
                {...form.register('core_theme')}
              />
              {fieldError.core_theme ? <p className="text-xs text-destructive">{fieldError.core_theme.message}</p> : null}
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('project.wizard.contentType')}</Label>
                <Select value={form.watch('content_type')} onValueChange={() => undefined}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fiction">{t('project.wizard.contentTypes.fiction')}</SelectItem>
                    <SelectItem value="nonfiction" disabled>
                      {t('project.wizard.contentTypes.nonfiction')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('project.wizard.lengthForm')}</Label>
                <Select
                  value={form.watch('length_form')}
                  onValueChange={(value) => chooseLength(value as ProjectWizardValues['length_form'])}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="poetry" disabled>
                      {t('project.wizard.lengthForms.poetry')}
                    </SelectItem>
                    <SelectItem value="short">{t('project.wizard.lengthForms.short')}</SelectItem>
                    <SelectItem value="medium" disabled>
                      {t('project.wizard.lengthForms.medium')}
                    </SelectItem>
                    <SelectItem value="long">{t('project.wizard.lengthForms.long')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="sm:col-span-2 text-xs text-muted-foreground">{t('project.wizard.axisHint')}</p>
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('project.wizard.contentLanguage')}</Label>
                <Select
                  value={form.watch('content_language')}
                  onValueChange={(value) => form.setValue('content_language', value, { shouldDirty: true })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_LANGUAGES.map((option) => (
                      <SelectItem key={option.code} value={option.code}>
                        {t(`languageNames.${option.code}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('project.wizard.contentLanguageHint')}</p>
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('project.genre')}</Label>
                {featuredTemplates.length ? (
                  <div className="flex flex-wrap gap-2">
                    {featuredTemplates.map((template) => (
                      <Button
                        key={template.genre_key}
                        type="button"
                        variant={selectedGenreKey === template.genre_key ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => form.setValue('genre_key', template.genre_key, { shouldDirty: true })}
                      >
                        {resolveLocalized(template.payload.genre_label, lang)}
                      </Button>
                    ))}
                  </div>
                ) : null}
                <Select
                  value={selectedGenreKey}
                  onValueChange={(value) => form.setValue('genre_key', value, { shouldDirty: true })}
                  disabled={templatesQuery.isLoading}
                >
                  <SelectTrigger className="w-full" aria-invalid={Boolean(fieldError.genre_key)}>
                    <SelectValue placeholder={templatesQuery.isLoading ? t('common.loading') : t('project.wizard.genrePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(templatesQuery.data ?? []).map((template) => (
                      <SelectItem key={template.genre_key} value={template.genre_key}>
                        {resolveLocalized(template.payload.genre_label, lang)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError.genre_key ? <p className="text-xs text-destructive">{fieldError.genre_key.message}</p> : null}
                {selectedTemplate ? (
                  <p className="text-xs text-muted-foreground">
                    已按「{genreLabel}」预置世界观、主角范式、驱动力组合和结构化创作提示。
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-target">{t('project.targetWords')}</Label>
                <Controller
                  control={form.control}
                  name="target_words"
                  render={({ field }) => (
                    <NumberInput
                      id="project-target"
                      min={1}
                      aria-invalid={Boolean(fieldError.target_words)}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                  )}
                />
                {fieldError.target_words ? <p className="text-xs text-destructive">{fieldError.target_words.message}</p> : null}
              </div>
            </section>

            <section className="space-y-3">
              <Label>{t('project.wizard.secondaryDrivers')}</Label>
              <Popover open={driverOpen} onOpenChange={setDriverOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between">
                    <span className="truncate">
                      {secondaryDrivers.length
                        ? t('project.wizard.selectedDriverCount', { count: secondaryDrivers.length })
                        : t('project.wizard.secondaryDriversPlaceholder')}
                    </span>
                    <ChevronsUpDown className="size-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[min(38rem,calc(100vw-2rem))] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('project.wizard.driverSearch')} />
                    <CommandList>
                      <CommandEmpty>{t('project.wizard.driverEmpty')}</CommandEmpty>
                      <CommandGroup>
                        {secondaryDriverOptions.map((driver) => {
                          const selected = secondaryDrivers.includes(driver.key)
                          // 按作品语言显示 native 驱动力名 (与 genre_label 用 contentLang 一致):
                          // 韩/日小说显示韩/日驱动力, 作者所见即送 LLM 的内容语言值。缺失语言 fallback en。
                          const label = secondaryDriverLabel(driver, contentLang)
                          return (
                            <CommandItem key={driver.key} value={label} onSelect={() => toggleDriver(driver.key)}>
                              <Check className={cn('size-4', selected ? 'opacity-100' : 'opacity-0')} />
                              {label}
                              {payload?.default_driver_combo?.includes(driver.key) ? (
                                <Badge variant="secondary" className="ml-auto">
                                  {t('project.wizard.recommended')}
                                </Badge>
                              ) : null}
                            </CommandItem>
                          )
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <div className="flex min-h-7 flex-wrap gap-2">
                {secondaryDrivers.map((driver) => (
                  <Badge key={driver} variant="secondary" className="gap-1">
                    {secondaryDriverLabel(secondaryDriverByKey.get(driver), contentLang) || driver}
                    <button type="button" onClick={() => toggleDriver(driver)} aria-label={t('project.wizard.removeDriver')}>
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-medium">{t('project.wizard.logline')}</h2>
                  <p className="text-sm text-muted-foreground">
                    {loglinePrompt || t('project.wizard.loglineHint')}
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => runAssist('logline')} disabled={isAssisting}>
                  {isAssisting && assistMutation.variables === 'logline' ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Sparkles className="size-4" />
                  )}
                  {t('project.wizard.aiLogline')}
                </Button>
              </div>
              <textarea
                className={textAreaClassName}
                maxLength={600}
                placeholder={loglinePrompt || t('project.wizard.loglinePlaceholder')}
                {...form.register('logline')}
              />
              {fieldError.logline ? <p className="text-xs text-destructive">{fieldError.logline.message}</p> : null}
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="project-world-time">{t('detail.timePeriod')}</Label>
                <Input id="project-world-time" placeholder={worldTimePeriod} {...form.register('world_time_period')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-world-location">{t('detail.location')}</Label>
                <Input id="project-world-location" placeholder={worldLocation} {...form.register('world_location')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-world-atmosphere">{t('detail.atmosphere')}</Label>
                <Input id="project-world-atmosphere" placeholder={worldAtmosphere} {...form.register('world_atmosphere')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-world-rules">{worldRulesLabel}</Label>
                <Input id="project-world-rules" placeholder={resolveLocalized(payload?.power_or_rule_system?.name, contentLang)} {...form.register('world_rules')} />
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="project-title">{t('project.name')}</Label>
                <Input id="project-title" maxLength={120} placeholder={t('project.wizard.titlePlaceholder')} {...form.register('title')} />
                {fieldError.title ? <p className="text-xs text-destructive">{fieldError.title.message}</p> : null}
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label htmlFor="project-description">{t('project.description')}</Label>
                  <VoiceInputButton
                    value={form.watch('description')}
                    language={contentLang}
                    onChange={(nextValue) => form.setValue('description', nextValue, { shouldDirty: true })}
                  />
                </div>
                <Input
                  id="project-description"
                  maxLength={800}
                  placeholder={t('project.wizard.descriptionPlaceholder')}
                  {...form.register('description')}
                />
                {fieldError.description ? <p className="text-xs text-destructive">{fieldError.description.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-protagonist-name">{t('project.wizard.protagonistName')}</Label>
                <Input id="project-protagonist-name" {...form.register('protagonist_name')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-protagonist-name-en">{t('project.wizard.protagonistNameEn')}</Label>
                <Input id="project-protagonist-name-en" {...form.register('protagonist_name_en')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-protagonist-motivation">{t('project.wizard.protagonistMotivation')}</Label>
                <Input id="project-protagonist-motivation" {...form.register('protagonist_motivation')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-protagonist-flaw">{t('project.wizard.protagonistInnerFlaw')}</Label>
                <Input
                  id="project-protagonist-flaw"
                  placeholder={protagonistInnerFlaw}
                  {...form.register('protagonist_inner_flaw')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-protagonist-identity">{t('project.wizard.protagonistIdentity')}</Label>
                <Input
                  id="project-protagonist-identity"
                  placeholder={protagonistStartSituation}
                  {...form.register('protagonist_identity_situation')}
                />
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="project-antagonist">{t('project.wizard.antagonistSource')}</Label>
                <Input id="project-antagonist" maxLength={300} {...form.register('antagonist_source')} />
                {fieldError.antagonist_source ? <p className="text-xs text-destructive">{fieldError.antagonist_source.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-perspective">{t('detail.narrativePerspective')}</Label>
                <Input
                  id="project-perspective"
                  maxLength={120}
                  placeholder={t('project.wizard.perspectivePlaceholder')}
                  {...form.register('narrative_perspective')}
                />
                {fieldError.narrative_perspective ? <p className="text-xs text-destructive">{fieldError.narrative_perspective.message}</p> : null}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="project-tone">{t('project.wizard.toneKeynote')}</Label>
                <Input id="project-tone" maxLength={200} {...form.register('tone_keynote')} />
                {fieldError.tone_keynote ? <p className="text-xs text-destructive">{fieldError.tone_keynote.message}</p> : null}
              </div>
            </section>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? t('project.creating') : t('common.create')}
              </Button>
            </DialogFooter>
              </>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assistState)} onOpenChange={(nextOpen) => !nextOpen && setAssistState(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('project.wizard.aiSuggestions')}</DialogTitle>
            <DialogDescription>
              {assistState?.source === 'template_fallback'
                ? t('project.wizard.aiFallbackSource')
                : t('project.wizard.aiLlmSource')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(assistState?.suggestions ?? []).map((suggestion, index) => (
              <div key={`${suggestion.angle}-${index}`} className="rounded-lg border p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{suggestion.angle}</p>
                  <Button type="button" size="sm" onClick={() => applySuggestion(suggestion)}>
                    {t('project.wizard.useSuggestion')}
                  </Button>
                </div>
                <p className="text-sm leading-6 text-muted-foreground">{suggestion.text}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAssistState(null)}>
              {t('project.wizard.discardSuggestions')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
